import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} StickyOverlayCardHeadingRefs
 * @property {HTMLElement} heading - The overlay card heading element
 * @property {HTMLElement} placeholder - The placeholder element
 */

/**
 * Sticky Overlay Card Heading Component
 *
 * Creates sticky behavior for services-overlay-card__heading elements within their parent wrapper.
 * The heading becomes fixed when it reaches the header height and stays fixed until
 * the bottom of its parent wrapper reaches the header.
 *
 * @extends {Component<StickyOverlayCardHeadingRefs>}
 */
class StickyOverlayCardHeading extends Component {
  /** @type {number|null} */
  #scrollAnimationFrame = null;

  /** @type {number|null} */
  #resizeTimeout = null;

  /** @type {number} */
  #originalTop = 0;

  /** @type {HTMLElement|null} */
  #wrapper = null;

  /** @type {boolean} */
  #isSticky = false;

  /** @type {boolean} */
  #isAtBottom = false;

  /** @type {boolean} */
  #isInitialized = false;

  /** @type {number|null} */
  #lastLeft = null;

  /** @type {number|null} */
  #lastWidth = null;

  /** @type {number|null} */
  #lastTop = null;

  /** @type {IntersectionObserver|null} */
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Find the wrapper element
    this.#wrapper = this.querySelector('[data-sticky-wrapper]');

    if (!this.#wrapper || !this.refs.heading) {
      this.#isInitialized = false;
      return;
    }

    this.#isInitialized = true;

    // Store original position and set placeholder height
    this.#storeOriginalPosition();

    // Listen for scroll and resize events
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || entry.isIntersecting) return;
        if (entry.boundingClientRect.bottom <= 0) {
          this.#updateStickyState();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0 }
    );
    this.#intersectionObserver.observe(this.#wrapper);

    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize, { passive: true });

    // Initial update
    this.#updateStickyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this.#isInitialized) return;

    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);

    if (this.#scrollAnimationFrame) {
      cancelAnimationFrame(this.#scrollAnimationFrame);
      this.#scrollAnimationFrame = null;
    }

    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = null;
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    if (this.refs.heading) {
      delete this.refs.heading.dataset.stickyHeading;
      document.dispatchEvent(new CustomEvent(ThemeEvents.stickyHeadingExited, { bubbles: true }));
    }
  }

  /**
   * Stores the original top position of the heading
   */
  #storeOriginalPosition() {
    if (!this.refs.heading) return;

    // Remove sticky classes to get accurate position
    this.refs.heading.classList.remove(
      'services-overlay-card__heading--sticky',
      'services-overlay-card__heading--at-bottom'
    );

    const rect = this.refs.heading.getBoundingClientRect();
    this.#originalTop = rect.top + window.scrollY;

    // Set placeholder height
    if (this.refs.placeholder) {
      this.refs.placeholder.style.setProperty(
        '--services-overlay-card-heading-height',
        `${this.refs.heading.offsetHeight}px`
      );
    }
  }

  /**
   * Gets the current header height from CSS variable
   * @returns {number}
   */
  #getHeaderHeight() {
    // --header-group-height is defined on body, not :root
    const headerHeightVar = getComputedStyle(document.body).getPropertyValue('--header-group-height').trim();
    return parseFloat(headerHeightVar) || 0;
  }

  /**
   * Updates position CSS variables only when values have changed
   * @param {number} left
   * @param {number} width
   * @param {number|null} top
   */
  #updatePositionStyles(left, width, top = null) {
    if (this.#lastLeft !== left) {
      this.refs.heading.style.setProperty('--services-overlay-card-sticky-left', `${left}px`);
      this.#lastLeft = left;
    }
    if (this.#lastWidth !== width) {
      this.refs.heading.style.setProperty('--services-overlay-card-sticky-width', `${width}px`);
      this.#lastWidth = width;
    }
    if (top !== null && this.#lastTop !== top) {
      this.refs.heading.style.setProperty('--services-overlay-card-sticky-top', `${top}px`);
      this.#lastTop = top;
    }
  }

  /**
   * Updates the sticky state based on scroll position
   */
  #updateStickyState() {
    if (!this.refs.heading || !this.#wrapper) return;

    const headerHeight = this.#getHeaderHeight();
    const wrapperRect = this.#wrapper.getBoundingClientRect();
    const headingHeight = this.refs.heading.offsetHeight;
    const scrollY = window.scrollY;

    // Distance from original position to where it should become sticky
    const stickyTrigger = this.#originalTop - headerHeight;

    // When the wrapper bottom reaches the header + heading height, stop being sticky
    const wrapperBottomFromTop = wrapperRect.bottom;
    const stopStickyAt = headerHeight + headingHeight;

    // Wrapper has scrolled completely above viewport, unstick so header can go transparent
    const wrapperStillInView = wrapperRect.bottom > 0;

    // Should the heading be sticky?
    const shouldBeSticky = scrollY >= stickyTrigger && wrapperRect.top < headerHeight && wrapperStillInView;

    // Should the heading be at the bottom of its wrapper?
    const isAtBottom = wrapperBottomFromTop <= stopStickyAt;

    // Apply states
    if (isAtBottom && shouldBeSticky) {
      if (!this.#isAtBottom) {
        this.#isAtBottom = true;
        this.#isSticky = false;
        this.refs.heading.classList.remove('services-overlay-card__heading--sticky');
        this.refs.heading.classList.add('services-overlay-card__heading--at-bottom');
        this.refs.heading.dataset.stickyHeading = 'true';
        document.dispatchEvent(new CustomEvent(ThemeEvents.stickyHeadingEntered, { bubbles: true }));
      }
      // Calculate position so heading appears at bottom of wrapper
      const topPosition = wrapperBottomFromTop - headingHeight;
      this.#updatePositionStyles(wrapperRect.left, wrapperRect.width, topPosition);
    } else if (shouldBeSticky) {
      if (!this.#isSticky || this.#isAtBottom) {
        this.#isSticky = true;
        this.#isAtBottom = false;
        this.refs.heading.classList.add('services-overlay-card__heading--sticky');
        this.refs.heading.classList.remove('services-overlay-card__heading--at-bottom');
        this.refs.heading.dataset.stickyHeading = 'true';
        document.dispatchEvent(new CustomEvent(ThemeEvents.stickyHeadingEntered, { bubbles: true }));
      }
      // Update horizontal position while sticky
      this.#updatePositionStyles(wrapperRect.left, wrapperRect.width);
    } else {
      if (this.#isSticky || this.#isAtBottom) {
        this.#isSticky = false;
        this.#isAtBottom = false;
        this.refs.heading.classList.remove(
          'services-overlay-card__heading--sticky',
          'services-overlay-card__heading--at-bottom'
        );
        delete this.refs.heading.dataset.stickyHeading;
        document.dispatchEvent(new CustomEvent(ThemeEvents.stickyHeadingExited, { bubbles: true }));
      }
    }
  }

  /**
   * Handles scroll events with throttling
   */
  #handleScroll = () => {
    if (this.#scrollAnimationFrame) return;

    this.#scrollAnimationFrame = requestAnimationFrame(() => {
      if (!this.isConnected) {
        this.#scrollAnimationFrame = null;
        return;
      }

      this.#updateStickyState();
      this.#scrollAnimationFrame = null;
    });
  };

  /**
   * Handles resize events with debouncing
   */
  #handleResize = () => {
    if (!this.isConnected) return;

    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
    }

    this.#resizeTimeout = setTimeout(() => {
      if (!this.isConnected) return;

      // Recalculate original position on resize
      this.#storeOriginalPosition();
      this.#updateStickyState();
      this.#resizeTimeout = null;
    }, 100);
  };
}

customElements.define('sticky-overlay-card-heading', StickyOverlayCardHeading);
